<template>
  <div ref="root" v-loading="loading" class="customer-class-page">
    <el-row class="m-20" type="flex" justify="space-between" align="middle">
      <el-col>
        <span class="fs-14">Classes clients</span>
      </el-col>
      <el-col class="text-right">
        <el-button type="primary" @click="openCreateDialog">Ajouter une classe</el-button>
      </el-col>
    </el-row>

    <div class="table-wrapper">
      <el-table
        ref="customerClassTable"
        :data="customerClasses"
        border
        stripe
        fit
        class="full-width-table"
        empty-text="Aucune classe client">
      <el-table-column prop="code" label="Nom" min-width="160" />
      <el-table-column prop="description" label="Description" min-width="260" />
      <el-table-column label="Actif" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.active ? 'success' : 'info'" size="mini">
            {{ scope.row.active ? 'Oui' : 'Non' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Nb clients" width="110" align="right">
        <template slot-scope="scope">
          <el-link type="primary" @click="goToCustomersByClass(scope.row)">
            {{ Number(scope.row.customerCount || 0) }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column label="Permis" width="120" align="center">
        <template slot-scope="scope">
          <el-tag :type="hasPermit(scope.row.ind) ? 'success' : 'info'" size="mini">
            {{ hasPermit(scope.row.ind) ? 'Oui' : 'Non' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="200" align="center">
        <template slot-scope="scope">
          <el-tooltip content="Modifier" placement="top">
            <el-button size="mini" circle @click="openEditDialog(scope.row)">
              <i class="mdi mdi-lead-pencil fs-14"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip :content="scope.row.active ? 'Désactiver' : 'Réactiver'" placement="top">
            <el-button
              size="mini"
              circle
              :type="scope.row.active ? 'warning' : 'success'"
              @click="toggleActive(scope.row)">
              <i class="mdi mdi-toggle-switch-off-outline fs-14"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip
            v-if="!scope.row.active && Number(scope.row.customerCount || 0) === 0"
            content="Supprimer"
            placement="top">
            <el-button
              size="mini"
              circle
              type="danger"
              @click="deleteCustomerClass(scope.row)">
              <i class="mdi mdi-delete fs-14"></i>
            </el-button>
          </el-tooltip>
        </template>
      </el-table-column>
      </el-table>
    </div>

    <el-drawer
      :title="isEdit ? 'Modifier une classe client' : 'Ajouter une classe client'"
      :visible.sync="dialogVisible"
      size="520px"
      :destroy-on-close="false"
      direction="rtl">
      <el-form ref="form" :model="form" :rules="rules" label-width="150px">
        <el-form-item label="Nom" prop="code">
          <el-input v-model="form.code" maxlength="120" />
        </el-form-item>
        <el-form-item label="Description" prop="description">
          <el-input v-model="form.description" maxlength="255" />
        </el-form-item>
        <el-form-item label="Détient un permis" prop="hasPermit">
          <el-switch v-model="form.hasPermit" active-text="Oui" inactive-text="Non"></el-switch>
        </el-form-item>
        <el-form-item v-if="isEdit" label="Actif" prop="active">
          <el-switch v-model="form.active" active-text="Oui" inactive-text="Non"></el-switch>
        </el-form-item>
        <el-form-item v-if="isEdit" label="Nb clients associés">
          <strong>{{ form.customerCount }}</strong>
        </el-form-item>
      </el-form>

      <div class="drawer-footer">
        <el-button @click="dialogVisible = false">Annuler</el-button>
        <el-button type="primary" :loading="saving" @click="save">Enregistrer</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import controlDataAPI from '@/services/API/controlData';

export default {
  name: 'CustomerClassController',
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      isEdit: false,
      customerClasses: [],
      form: {
        id: null,
        code: '',
        description: '',
        hasPermit: false,
        active: true,
        customerCount: 0,
      },
      rules: {
        code: [{ required: true, message: 'Le code est requis', trigger: 'blur' }],
        description: [{ required: true, message: 'La description est requise', trigger: 'blur' }],
      },
      resizeObserver: null,
    };
  },
  methods: {
    invalidateCustomerClassStoreCache() {
      return this.$store.dispatch('controlData/invalidateCustomerClasses');
    },
    refreshTableLayout() {
      const rerender = () => {
        if (this.$refs.customerClassTable && this.$refs.customerClassTable.doLayout) {
          this.$refs.customerClassTable.doLayout();
        }
      };

      this.$nextTick(() => {
        rerender();
        setTimeout(rerender, 80);
        setTimeout(rerender, 220);
      });
    },
    startObserveSize() {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.root) return;
      this.resizeObserver = new ResizeObserver(() => this.refreshTableLayout());
      this.resizeObserver.observe(this.$refs.root);
    },
    stopObserveSize() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    hasPermit(ind) {
      const value = String(ind || '').toUpperCase();
      return ['LICENCIE', 'LICENCEE', 'YES', 'Y', 'TRUE', '1'].includes(value);
    },
    fetchCustomerClasses() {
      this.loading = true;
      return controlDataAPI.fetchCustomerClassesAdmin({ includeInactive: true })
        .then((rows) => {
          this.customerClasses = rows || [];
          this.refreshTableLayout();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreateDialog() {
      this.isEdit = false;
      this.form = {
        id: null,
        code: '',
        description: '',
        hasPermit: false,
        active: true,
        customerCount: 0,
      };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate());
    },
    openEditDialog(row) {
      this.isEdit = true;
      this.form = {
        id: row.id,
        code: row.code || '',
        description: row.description || '',
        hasPermit: this.hasPermit(row.ind),
        active: row.active === true,
        customerCount: Number(row.customerCount || 0),
      };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate());
    },
    toggleActive(row) {
      const nextActive = !(row.active === true);
      const payload = {
        code: row.code,
        description: row.description,
        hasPermit: this.hasPermit(row.ind),
        active: nextActive,
      };

      this.saving = true;
      return controlDataAPI.updateCustomerClass(row.id, payload)
        .then(() => {
          this.invalidateCustomerClassStoreCache();
          this.$message.success(nextActive ? 'Classe client réactivée' : 'Classe client désactivée');
          return this.fetchCustomerClasses();
        })
        .catch((err) => {
          console.log(err);
          this.$message.error('Une erreur est survenue');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    deleteCustomerClass(row) {
      this.$confirm('Supprimer cette classe client ? Cette action est définitive.', 'Confirmation', {
        confirmButtonText: 'Supprimer',
        cancelButtonText: 'Annuler',
        type: 'warning',
      })
        .then(() => {
          this.saving = true;
          return controlDataAPI.deleteCustomerClass(row.id)
            .then(() => {
              this.invalidateCustomerClassStoreCache();
              this.$message.success('Classe client supprimée');
              return this.fetchCustomerClasses();
            })
            .catch((err) => {
              console.log(err);
              this.$message.error('Impossible de supprimer la classe client');
            })
            .finally(() => {
              this.saving = false;
            });
        })
        .catch(() => {});
    },
    goToCustomersByClass(row) {
      const classId = Number(row.id);
      if (!classId) return;

      this.$store.dispatch('customers/resetFilters')
        .then(() => this.$store.dispatch('customers/setCheckedCustomerClasses', [classId]))
        .then(() => this.$router.push('/customers'));
    },
    save() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        this.saving = true;

        const payload = {
          code: this.form.code,
          description: this.form.description,
          hasPermit: this.form.hasPermit,
          active: this.form.active,
        };

        const savePromise = this.isEdit
          ? controlDataAPI.updateCustomerClass(this.form.id, payload)
          : controlDataAPI.createCustomerClass(payload);

        savePromise
          .then(() => {
            this.invalidateCustomerClassStoreCache();
            this.$message.success(this.isEdit ? 'Classe client modifiée' : 'Classe client ajoutée');
            this.dialogVisible = false;
            return this.fetchCustomerClasses();
          })
          .catch((err) => {
            console.log(err);
            this.$message.error('Une erreur est survenue');
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
  },
  mounted() {
    this.fetchCustomerClasses();
    this.refreshTableLayout();
    this.startObserveSize();
  },
  activated() {
    this.refreshTableLayout();
  },
  beforeDestroy() {
    this.stopObserveSize();
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/scss/_variables';

.customer-class-page {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.full-width-table {
  width: 100%;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 20px;
}

::v-deep .el-drawer__body {
  padding: 0 20px 20px;
}
</style>
